import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { FEEXPAY_CURRENCY, computeFeexPayAmount, type FeexPayConfig } from '@/lib/feexpay';
import { fetchSubscriptionPlans } from '@/lib/subscriptionPlans';
import { convertAmount, normalizeCurrency } from '@/lib/currency';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Mon abonnement (tableau de bord marchand).
// Historique des paiements d'abonnement : statut, référence,
// montant (devise du compte + équivalent XOF FeexPay), date, formule.
// ─────────────────────────────────────────────────────────────

interface SubscriptionRow {
  id: number;
  plan: string | null;
  period: string | null;
  prix: number | null;
  monaie: string | null;
  status: string | null;
  payment_method: string | null;
  last_payment_id: string | null;
  start_date: string | null;
  next_billing_date: string | null;
  note: string | null;
  created_at: string | null;
}

const STATUS_META: Record<string, { label: string; className: string; icon: string }> = {
  paid: { label: 'Payé', className: 'bg-accent-100 text-accent-700', icon: 'ri-checkbox-circle-line' },
  pending: { label: 'En attente', className: 'bg-secondary-100 text-secondary-700', icon: 'ri-time-line' },
  failed: { label: 'Échoué', className: 'bg-red-50 text-red-600', icon: 'ri-close-circle-line' },
  expired: { label: 'Expiré', className: 'bg-background-100 text-foreground-500', icon: 'ri-forbid-2-line' },
  cancelled: { label: 'Annulé', className: 'bg-background-100 text-foreground-500', icon: 'ri-forbid-2-line' },
};

function statusMeta(status: string | null) {
  return STATUS_META[(status || '').toLowerCase()] || { label: status || '—', className: 'bg-background-100 text-foreground-600', icon: 'ri-information-line' };
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function methodLabel(method: string | null): string {
  if (!method) return '—';
  const map: Record<string, string> = { feexpay: 'FeexPay', stripe: 'Stripe', paypal: 'PayPal', cmi: 'CMI', wave: 'Wave', flutterwave: 'Flutterwave' };
  return map[method] || method;
}

/** Tentative de paiement FeexPay (journal payment_attempt_logs). */
interface AttemptRow {
  id: number;
  status: string | null;
  amount: number | string | null;
  currency: string | null;
  gateway_amount: number | string | null;
  gateway_currency: string | null;
  operator: string | null;
  country: string | null;
  phone: string | null;
  reference: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string | null;
}

/** Motifs opérateurs connus → phrase compréhensible par le marchand. */
const ATTEMPT_REASON_LABELS: Record<string, string> = {
  LOW_BALANCE_OR_PAYEE_LIMIT_REACHED_OR_NOT_ALLOWED:
    'Fonds insuffisants ou plafond atteint sur le compte Mobile Money.',
  INSUFFICIENT_FUNDS: 'Fonds insuffisants sur le compte Mobile Money.',
  INVALID_PHONE_NUMBER: 'Numéro Mobile Money invalide.',
  PAYER_NOT_FOUND: 'Compte Mobile Money introuvable.',
  USER_CANCELLED: 'Paiement annulé sur le téléphone.',
  TIMEOUT: 'Délai dépassé : la demande n’a pas été validée sur le téléphone.',
};

function attemptReason(row: AttemptRow): string {
  const code = (row.error_code || '').toUpperCase();
  const mapped = ATTEMPT_REASON_LABELS[code];
  const raw = (row.error_message || '').replace(/^Paiement refusé —\s*/i, '').trim();
  if (mapped && (!raw || /^(paiement refusé|paiement non réussi|échec)/i.test(raw))) return mapped;
  if (raw) return raw;
  return mapped || 'Motif non communiqué par l’opérateur.';
}

export default function DashboardAbonnementPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [attempts, setAttempts] = useState<AttemptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [xofRate, setXofRate] = useState<number>(1);
  const [planFreeMonths, setPlanFreeMonths] = useState(0);

  const monaie = normalizeCurrency(user?.monaie || 'MAD');

  /** Convertit un montant dans la devise du commerce. */
  const toMerchant = (amount: number, from?: string | null): number => {
    const value = Number(amount) || 0;
    const source = normalizeCurrency(from || monaie);
    if (source === monaie) return value;
    const converted = convertAmount(value, source, monaie);
    return converted != null ? converted : value;
  };

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const [subsRes, feexRes, plans] = await Promise.all([
        supabase
          .from('package_subscriptions')
          .select('id, plan, period, prix, monaie, status, payment_method, last_payment_id, start_date, next_billing_date, note, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(100),
        supabase.from('payment_providers').select('config').eq('provider', 'feexpay').maybeSingle(),
        fetchSubscriptionPlans(),
      ]);
      const subsRows = (subsRes.data || []) as SubscriptionRow[];
      setRows(subsRows);
      // Mois gratuits offerts par la formule rattachée au marchand.
      const currentSub = subsRows.find((r) => (r.status || '').toLowerCase() === 'paid') || subsRows[0];
      const matchedPlan = currentSub?.plan ? plans.find((p) => p.nom === currentSub.plan) : undefined;
      setPlanFreeMonths(matchedPlan?.moisGratuits || 0);
      const cfg = (feexRes.data?.config || {}) as FeexPayConfig;
      const rate = Number(cfg.xof_rate);
      setXofRate(Number.isFinite(rate) && rate > 0 ? rate : 1);

      // Tentatives FeexPay : on cherche par identifiant de compte ET par email
      // (le journal stocke l'identifiant d'authentification).
      const attemptSelect =
        'id, status, amount, currency, gateway_amount, gateway_currency, operator, country, phone, reference, error_code, error_message, created_at';
      const [byId, byMail] = await Promise.all([
        supabase
          .from('payment_attempt_logs')
          .select(attemptSelect)
          .eq('user_id', String(user.id))
          .order('created_at', { ascending: false })
          .limit(40),
        user.email
          ? supabase
            .from('payment_attempt_logs')
            .select(attemptSelect)
            .eq('user_label', user.email)
            .order('created_at', { ascending: false })
            .limit(40)
          : Promise.resolve({ data: [] as AttemptRow[], error: null }),
      ]);
      const mergedAttempts = new Map<number, AttemptRow>();
      [...((byId.data || []) as AttemptRow[]), ...((byMail.data || []) as AttemptRow[])].forEach((row) => {
        mergedAttempts.set(Number(row.id), row);
      });
      setAttempts(
        [...mergedAttempts.values()].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')).reverse(),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const paidRows = rows.filter((r) => (r.status || '').toLowerCase() === 'paid');
  const totalPaid = paidRows.reduce((s, r) => s + toMerchant(Number(r.prix) || 0, r.monaie), 0);
  const nextBilling = rows.find((r) => r.next_billing_date && (r.status || '').toLowerCase() === 'paid');
  const referralFreeMonths = user?.parrainage_mois_gratuits || 0;
  const totalFreeMonths = planFreeMonths + referralFreeMonths;
  const retryPlanName = rows[0]?.plan || '';
  const retryHref = `/dashboard/abonnement/choisir?retry=1&method=feexpay${retryPlanName ? `&plan=${encodeURIComponent(retryPlanName)}` : ''}`;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mon abonnement</h2>
          <p className="text-sm text-foreground-500 mt-1">Formules, paiements et échéances de votre compte Zifek.</p>
        </div>
        <Link
          to="/dashboard/abonnement/choisir"
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>Choisir / renouveler
        </Link>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center gap-2 text-foreground-500 text-sm mb-2">
            <i className="ri-wallet-3-line text-accent-500"></i>Total payé
          </div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{totalPaid.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} <span className="text-base font-medium text-foreground-500">{monaie}</span></div>
          <div className="text-xs text-foreground-400 mt-1">≈ {computeFeexPayAmount(totalPaid, xofRate, monaie).toLocaleString('fr-FR')} {FEEXPAY_CURRENCY}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center gap-2 text-foreground-500 text-sm mb-2">
            <i className="ri-checkbox-circle-line text-primary-500"></i>Abonnements confirmés
          </div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{paidRows.length}</div>
          <div className="text-xs text-foreground-400 mt-1">Paiements validés</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center gap-2 text-foreground-500 text-sm mb-2">
            <i className="ri-gift-line text-accent-500"></i>Mois gratuits
          </div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{totalFreeMonths}</div>
          <div className="text-xs text-foreground-400 mt-1">
            {totalFreeMonths > 0 ? 'Offerts sur votre abonnement' : 'Aucun mois gratuit en cours'}
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center gap-2 text-foreground-500 text-sm mb-2">
            <i className="ri-calendar-check-line text-secondary-500"></i>Prochaine échéance
          </div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{formatDate(nextBilling?.next_billing_date || null)}</div>
          <div className="text-xs text-foreground-400 mt-1">{nextBilling?.plan || 'Aucune échéance planifiée'}</div>
        </div>
      </div>

      {totalFreeMonths > 0 && (
        <div className="flex items-start gap-3 px-4 py-3 bg-accent-50 border border-accent-200 rounded-lg mb-6">
          <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-gift-2-line"></i>
          </div>
          <div className="text-sm">
            <p className="font-semibold text-foreground-900">
              Vous bénéficiez de {totalFreeMonths} mois gratuit{totalFreeMonths > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-foreground-600 mt-0.5">
              {planFreeMonths > 0 && <>Formule : {planFreeMonths} mois offert{planFreeMonths > 1 ? 's' : ''}. </>}
              {referralFreeMonths > 0 && <>Parrainage : {referralFreeMonths} mois offert{referralFreeMonths > 1 ? 's' : ''}.</>}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {/* Historique */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-background-200/70">
          <i className="ri-history-line text-accent-500"></i>
          <h3 className="text-sm font-semibold text-foreground-900">Historique des paiements</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-bank-card-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Aucun paiement d&apos;abonnement pour le moment.</p>
            <Link to="/dashboard/abonnement/choisir" className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">Choisir une formule →</Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                  <th className="py-2 px-5 font-medium">Date</th>
                  <th className="py-2 px-3 font-medium">Formule</th>
                  <th className="py-2 px-3 font-medium">Méthode</th>
                  <th className="py-2 px-3 font-medium">Montant</th>
                  <th className="py-2 px-3 font-medium">Référence</th>
                  <th className="py-2 px-5 font-medium">Statut</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = statusMeta(r.status);
                  const xof = computeFeexPayAmount(toMerchant(Number(r.prix) || 0, r.monaie), xofRate, monaie);
                  return (
                    <tr key={r.id} className="border-b border-background-200/50 last:border-0">
                      <td className="py-2.5 px-5 text-foreground-500 whitespace-nowrap">{formatDate(r.created_at)}</td>
                      <td className="py-2.5 px-3">
                        <span className="text-foreground-800 font-medium">{r.plan || '—'}</span>
                        <span className="text-foreground-400 text-xs ml-1">· {r.period === 'yearly' ? 'annuel' : 'mensuel'}</span>
                      </td>
                      <td className="py-2.5 px-3 text-foreground-600 whitespace-nowrap">{methodLabel(r.payment_method)}</td>
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <div className="font-semibold text-foreground-900">{(Number(r.prix) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {r.monaie || monaie}</div>
                        <div className="text-[11px] text-foreground-400">
                          {normalizeCurrency(r.monaie || monaie) !== monaie && (
                            <>≈ {toMerchant(Number(r.prix) || 0, r.monaie).toLocaleString('fr-FR')} {monaie} · </>
                          )}
                          ≈ {xof.toLocaleString('fr-FR')} {FEEXPAY_CURRENCY}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-foreground-600 max-w-[220px]">
                        <code className="text-xs break-all">{r.last_payment_id || '—'}</code>
                      </td>
                      <td className="py-2.5 px-5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${meta.className}`}>
                          <i className={meta.icon}></i>{meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tentatives FeexPay : motif exact du refus */}
      <div className="mt-6 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-background-200/70">
          <div className="flex items-center gap-2">
            <i className="ri-secure-payment-line text-primary-500"></i>
            <h3 className="text-sm font-semibold text-foreground-900">Tentatives FeexPay</h3>
            <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium">{attempts.length}</span>
          </div>
          <Link
            to={retryHref}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200/70 transition-colors no-underline whitespace-nowrap"
          >
            <i className="ri-restart-line"></i>
            Relancer un paiement
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
          </div>
        ) : attempts.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-foreground-500">
            Aucune tentative FeexPay enregistrée pour le moment.
          </div>
        ) : (
          <ul className="divide-y divide-background-200/50">
            {attempts.map((a) => {
              const failed = /fail|declin|refus|error|cancel|expire/i.test(a.status || '');
              const amount = Number(a.gateway_amount ?? a.amount) || 0;
              const currency = a.gateway_currency || a.currency || FEEXPAY_CURRENCY;
              return (
                <li key={a.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap ${failed ? 'bg-red-50 text-red-600' : 'bg-accent-100 text-accent-700'}`}>
                          <i className={failed ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'}></i>
                          {failed ? 'Refusé' : (a.status || 'Reçu')}
                        </span>
                        <span className="text-xs text-foreground-500">{formatDate(a.created_at)}</span>
                        {a.operator && (
                          <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium whitespace-nowrap">{a.operator}</span>
                        )}
                        {a.phone && <span className="text-[11px] text-foreground-400">{a.phone}</span>}
                      </div>
                      <p className={`mt-1.5 text-sm ${failed ? 'text-red-600' : 'text-foreground-700'}`}>
                        {failed ? attemptReason(a) : 'Paiement transmis à l’opérateur.'}
                      </p>
                      {a.reference && (
                        <p className="mt-1 text-[11px] text-foreground-400 break-all">
                          Réf. <code>{a.reference}</code>
                          {a.country ? ` · ${a.country}` : ''}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-foreground-950 whitespace-nowrap">
                        {amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {currency}
                      </div>
                      {failed && (
                        <Link
                          to={retryHref}
                          className="inline-flex items-center gap-1 mt-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors"
                        >
                          <i className="ri-restart-line"></i>
                          Réessayer
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="mt-4 text-[11px] text-foreground-400 flex items-start gap-1.5 leading-relaxed">
        <i className="ri-information-line mt-0.5"></i>
        Les montants en {FEEXPAY_CURRENCY} sont une estimation pour les paiements FeexPay (Mobile Money / carte). Le montant exact est confirmé par l&apos;opérateur.
      </p>
    </div>
  );
}